import { ReactNode, useEffect, useState } from 'react'
import { FormProvider, useForm } from 'react-hook-form'

import { ApolloQueryResult } from '@apollo/client'
import { Box, Form } from '@factorialco/deprecated-design-system'
import { zodResolver } from '@hookform/resolvers/zod'
import {
  AllocationFragment,
  EvaluateExpensableQuery,
  EvaluateExpensableQueryVariables,
  ExpensableBudgetFragment,
  ExpenseDetailPageQuery,
  GetExpensablesQuery,
  SpendingAlertFragment,
} from 'generated/resources/graphql'
import { CurrencySelectorCurrencyFragment } from 'generated/resources/graphql'

import { observer } from 'mobx-react'

import Modal from 'modules/core/components/Modal'
import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useUnsafeEffect } from 'modules/core/hooks/useEffect'
import { useUnsafeMemo } from 'modules/core/hooks/useMemo'

import SubmitOcrAnimation from 'modules/expenses/components/common/SubmitOcrAnimation'
import ConfirmationScreen from 'modules/expenses/components/expenses/Detail/_Form/Common/ConfirmationScreen'
import EditExpenseFooterActions from 'modules/expenses/components/expenses/Detail/_Form/Common/EditExpenseFooterActions'
import SnackBarAlerts from 'modules/expenses/components/expenses/Detail/_Form/Common/SnackBarAlerts'
import { getExpensesFormDefaultValues } from 'modules/expenses/components/expenses/Detail/_Form/Expense/new/form/form'
import { ExpenseSchema } from 'modules/expenses/components/expenses/Detail/_Form/Expense/new/form/schema'
import { useBulkActions } from 'modules/expenses/components/expenses/Detail/hooks/useBulkActions'
import { useFilesPreviewer } from 'modules/expenses/components/expenses/Detail/hooks/useFilesPreviewer'
import { SpendingPolicesResultData } from 'modules/expenses/graphql/types'
import {
  ExpensableExpense,
  normalizeEffectiveOn,
  useDelete,
  useSendForApproval,
  useUpdate,
  useUpdateAndApprove,
  useUpdateFromDocument,
} from 'modules/expenses/hooks/useExpensable'
import { useExpenseAlerts } from 'modules/expenses/hooks/useExpenseAlerts'
import { CustomizationField } from 'modules/expenses/hooks/useExpensesCustomization'
import { useProjectsAssignedToEmployees } from 'modules/expenses/hooks/useProjects'
import { SignableOrganizationPerCountry } from 'modules/expenses/hooks/useTaxAgencyHomologations'
import { filterCustomizationFieldsByExpenseDocumentType } from 'modules/expenses/lib/customization_settings/filterByExpenseDocumentType'
import {
  isApprovedExpensable,
  isDraftExpensable,
  isManualExpensable,
  isPendingExpensable,
} from 'modules/expenses/lib/expensables/expensable'
import { buildEvaluatePolicyData } from 'modules/expenses/lib/spending_policies/evaluatePolicy'
import { Expensable } from 'modules/expenses/types/Expensable'
import { useBudgetOptions } from 'modules/finance/hooks/useBudgets'

import NewExpenseForm from './new'

interface ExpenseFormProps {
  expensable: Expensable
  expense: ExpensableExpense
  customizationFields: CustomizationField[]
  taxAgencyOrganization: SignableOrganizationPerCountry | null
  adeTermsAccepted?: boolean
  editable: boolean
  approvalsInformation?: ReactNode
  payrollSupplement: ReactNode
  onClose: () => void
  hasReportOnBehalfFeature: boolean
  hasProjectsFeature: boolean
  reimburseThroughPayrollEnabled: boolean
  reimburseThroughSepaEnabled: boolean
  refetchExpensable: () => Promise<ApolloQueryResult<GetExpensablesQuery>>
  refetchSpendingPolicyEvaluate?: (
    variables: EvaluateExpensableQueryVariables
  ) => Promise<ApolloQueryResult<EvaluateExpensableQuery>>
  setSpendingPolicyEvaluationResult: (policy: SpendingPolicesResultData | undefined) => void
  canUseSpendingPolicies: boolean
  canCreateExpensesForOthers: boolean
  canOverrideApprovalFlow: boolean
  canSetExpensableToPaid: boolean
  canReviewExpensable: boolean
  canCustomizeSettings: boolean
  canSeeEmployeeLegalEntity: boolean
  currencies?: CurrencySelectorCurrencyFragment[]
  canDeleteExpensable: boolean
  onClickSnackBar: () => void
  toggleAlert: () => void
  hideAlert: boolean
  budget?: ExpensableBudgetFragment | null
  budgetAllocation?: AllocationFragment | null
  spendingPolicyEvaluationResult?: SpendingPolicesResultData | null
  spendingAlerts?: SpendingAlertFragment[]
  registerDropHandler?: (onDrop: (files: File[]) => void) => void
  hasWorkflowSpendingPolicies: boolean
  hasCostCentersFeature: boolean
  approvalsV2Enabled: boolean
  approvalProcess: ApprovalProcessType | null
}

type ApprovalProcessType =
  ExpenseDetailPageQuery['expenses']['expensablesConnection']['edges'][0]['node']['approvalProcess']

const ExpenseForm = ({
  expensable,
  expense,
  customizationFields: unfilteredCustomizationFields,
  taxAgencyOrganization,
  adeTermsAccepted,
  editable,
  approvalsInformation,
  onClose,
  hasCostCentersFeature,
  hasReportOnBehalfFeature,
  hasProjectsFeature,
  reimburseThroughPayrollEnabled,
  reimburseThroughSepaEnabled,
  refetchExpensable,
  refetchSpendingPolicyEvaluate,
  setSpendingPolicyEvaluationResult,
  canUseSpendingPolicies,
  canCreateExpensesForOthers,
  canOverrideApprovalFlow,
  canSetExpensableToPaid,
  canReviewExpensable,
  canCustomizeSettings,
  canSeeEmployeeLegalEntity,
  currencies,
  canDeleteExpensable,
  onClickSnackBar,
  toggleAlert,
  hideAlert,
  budget,
  budgetAllocation,
  spendingPolicyEvaluationResult,
  spendingAlerts,
  registerDropHandler,
  hasWorkflowSpendingPolicies,
  approvalsV2Enabled,
  approvalProcess,
}: ExpenseFormProps) => {
  const { employee: currentEmployee } = useCurrentSession()

  const bulkActions = useBulkActions()
  const previewer = useFilesPreviewer()

  const { loading: updating } = useUpdate()
  const { loading: updatingAndApproving } = useUpdateAndApprove()
  const { loading: updatingFromDocument } = useUpdateFromDocument()
  const { loading: sendingForApproval } = useSendForApproval()
  const { loading: deleting } = useDelete()

  const [documentType, setDocumentType] = useState<string | undefined>(
    expense?.documentType ?? undefined
  )

  const customizationFields = useUnsafeMemo(
    () =>
      filterCustomizationFieldsByExpenseDocumentType(unfilteredCustomizationFields, documentType),
    [unfilteredCustomizationFields, documentType]
  )

  const isManual = isManualExpensable(expensable)
  const isDraft = isDraftExpensable(expensable)

  const submitting = updating || updatingAndApproving || sendingForApproval || deleting

  const isDraftManual = expensable && isManual && isDraft
  const syncableState = expensable?.syncableState?.[0] ?? null

  const { alertMessage, alertType } = useExpenseAlerts({
    expensableType: expensable?.type ?? 'expense',
    isDraftManual: isDraftManual ?? false,
    expensableAmount: Number(expensable.amount),
    reimbursableAmount: Number(expensable.reimbursableAmount),
    budget,
    budgetAllocation,
    spendingPolicy: spendingPolicyEvaluationResult,
    spendingAlerts,
    syncableState,
    hasWorkflowSpendingPolicies,
  })

  const showSnackBar =
    alertMessage.length > 0 &&
    !hideAlert &&
    canUseSpendingPolicies &&
    expensable &&
    (isDraft || isPendingExpensable(expensable) || isApprovedExpensable(expensable))

  const hasSignedDocuments = !!expensable?.expense?.signedDocument?.id

  const defaultValues = getExpensesFormDefaultValues({
    employee: currentEmployee,
    customizationFields,
    groupId: expensable?.groupId,
    expensable,
  })
  const [formEffectiveOn, setFormEffectiveOn] = useState(defaultValues.effectiveOn)
  const [formEmployeeId, setFormEmployeeId] = useState<number | null | undefined>(
    defaultValues.employeeId
  )
  const { employee } = useCurrentSession()
  const employeeId = formEmployeeId ?? employee?.id

  const { projectWorkers: newProjectWorkers, loading: newLoadingProjectWorkers } =
    useProjectsAssignedToEmployees({
      skip: false,
      employeeIds: employeeId ? [employeeId] : [],
    })

  const { budgetOptions: newBudgetOptions, loading: newLoadingBudgetOptions } = useBudgetOptions({
    effectiveOn: normalizeEffectiveOn(formEffectiveOn),
    employeeId,
    skip: false,
  })

  const expenseForm = useForm({
    resolver: zodResolver(
      ExpenseSchema({
        customizationFields,
        hasSignedDocuments,
        previewer,
        budgetOptions: newBudgetOptions,
        projectWorkers: newProjectWorkers,
        hasProjectsFeature,
        resourceType: 'expense',
        hasCostCentersFeature,
      })
    ),
    defaultValues: defaultValues,
    mode: 'onChange',
  })
  const { formState, trigger } = expenseForm
  const [showConfirmationScreen, setShowConfirmationScreen] = useState(false)
  const [editingFromConfirmation, setEditingFromConfirmation] = useState(false)
  const [scrollTo, setScrollTo] = useState<string | undefined>(undefined)

  const { isValid: isValidForm, isDirty } = formState

  useEffect(() => {
    if (!canUseSpendingPolicies || !refetchSpendingPolicyEvaluate) return

    const formValues = expenseForm.getValues()
    const formIsUpdated = expenseForm.formState.isDirty

    if (formIsUpdated) {
      const reimbursableAmount = Number(formValues.reimbursableAmount ?? 0)
      refetchSpendingPolicyEvaluate({
        amount:
          reimbursableAmount !== 0
            ? reimbursableAmount * 100
            : Number(formValues.amount ?? 0) * 100,
        currency: formValues.currency,
        effectiveOn: expensable?.effectiveOn || undefined,
        category: formValues.category || '',
        subcategory:
          formValues.subcategory && formValues.subcategory !== ''
            ? formValues.subcategory
            : undefined,
        companyId: String(expensable.company.id),
        expensableId: expensable.id,
        employeeId: Number(formValues.employee?.id),
      }).then((result: ApolloQueryResult<EvaluateExpensableQuery>) => {
        const policyEvaluation =
          result?.data?.finance.expenseSpendingPolicyEvaluationsConnection.nodes[0]

        const policyData = buildEvaluatePolicyData(policyEvaluation)
        setSpendingPolicyEvaluationResult(policyData)
      })
    }
  }, [
    canUseSpendingPolicies,
    refetchSpendingPolicyEvaluate,
    expenseForm,
    expensable.company.id,
    expensable?.effectiveOn,
    expensable.id,
    setSpendingPolicyEvaluationResult,
  ])

  const onDocumentTypeChange = (value: string) => {
    setDocumentType(value)
  }

  const onEffectiveOnChange = (value: string) => {
    setFormEffectiveOn(value)
  }

  useUnsafeEffect(() => {
    trigger().then((validationResult) => {
      if (editingFromConfirmation) {
        setShowConfirmationScreen(false)
        return
      }

      if (validationResult) {
        setShowConfirmationScreen(true)
      }
    })
  }, [customizationFields, trigger, editingFromConfirmation])

  const isSubmitting = submitting || bulkActions.submitting
  const showApprovalsInformation = !isSubmitting && !isDraft

  return (
    <Form padding="s0" notRounded>
      <Box gap="s32" paddingX="s24">
        <FormProvider {...expenseForm}>
          {showConfirmationScreen ? (
            <ConfirmationScreen
              expensable={expensable}
              customizationFields={customizationFields}
              resourceType="expense"
              onEdit={(scrollToSection?: string | undefined) => {
                setShowConfirmationScreen(false)
                setEditingFromConfirmation(true)
                setScrollTo(scrollToSection)
              }}
              editable={editable}
              adeTermsAccepted={adeTermsAccepted}
              taxAgencyOrganization={taxAgencyOrganization}
            />
          ) : (
            <NewExpenseForm
              customizationFields={customizationFields}
              canSeeEmployeeLegalEntity={canSeeEmployeeLegalEntity}
              canEditExpensesWithPendingStatus={editable}
              canCustomizeSettings={canCustomizeSettings}
              canCreateExpensesForOthers={canCreateExpensesForOthers}
              currencies={currencies}
              expensable={expensable}
              refetchExpensable={refetchExpensable}
              hasProjectsFeature={hasProjectsFeature}
              hasReportOnBehalfFeature={hasReportOnBehalfFeature}
              taxAgencyOrganization={taxAgencyOrganization}
              projectWorkers={newProjectWorkers}
              budgetOptions={newBudgetOptions}
              loadingProjectWorkers={newLoadingProjectWorkers}
              loadingBudgetOptions={newLoadingBudgetOptions}
              onDocumentTypeChange={onDocumentTypeChange}
              onEmployeeIdChange={setFormEmployeeId}
              onEffectiveOnChange={onEffectiveOnChange}
              scrollTo={scrollTo ?? undefined}
              registerDropHandler={registerDropHandler}
              hasCostCentersFeature={hasCostCentersFeature}
            />
          )}
        </FormProvider>
        {showApprovalsInformation && approvalsInformation}
      </Box>
      {updatingFromDocument && (
        <Modal illustration="document-folder-disorder-list" title="" locked>
          <Box
            flexDirection="column"
            justifyContent="center"
            alignItems="center"
            width="full"
            gap="s6"
            tabIndex={0}
          >
            <SubmitOcrAnimation operation="update" />
          </Box>
        </Modal>
      )}
      <SnackBarAlerts
        showSnackBar={showSnackBar}
        alertType={alertType}
        alertMessage={alertMessage}
        toggleAlert={toggleAlert}
        onClickLink={onClickSnackBar}
      />
      <EditExpenseFooterActions
        formType="expense"
        expensable={expensable}
        formValues={expenseForm.getValues()}
        customizationFields={customizationFields}
        canEdit={editable}
        canReviewExpensable={canReviewExpensable}
        canDeleteExpensable={canDeleteExpensable}
        canOverrideApprovalFlow={canOverrideApprovalFlow}
        canSetExpensableToPaid={canSetExpensableToPaid}
        reimburseThroughPayrollEnabled={reimburseThroughPayrollEnabled}
        reimburseThroughSepaEnabled={reimburseThroughSepaEnabled}
        taxAgencyOrganization={taxAgencyOrganization}
        formValid={isValidForm}
        formDirty={isDirty}
        approvalsV2Enabled={approvalsV2Enabled}
        approvalProcess={approvalProcess}
        trigger={trigger}
        onClose={onClose}
      />
    </Form>
  )
}
export default observer(ExpenseForm)
